/**
 * Shared shapes + tiny pure helpers for the job detail page and its
 * extracted cards (`detail/*.tsx`). Moved out of detail.tsx so the story
 * column and reference rail can import them without circular imports.
 */

export interface JobCrewMember {
    user_id: string;
    name: string | null;
    role: string;
    /** Per-person points mode chosen at add-time (flexible crew). */
    earning_mode?: "full" | "split_even" | "mirror";
}

/** One crew member on a historical appointment (Issue #442, Visit history). */
export interface JobAppointmentCrewMember {
    userId: string;
    name: string | null;
    role: string;
    pointsMode: string;
    isPrimary: boolean;
}

/** A single appointment row from GET /api/jobs/:id/appointments (Visit history).
 *  The append-only ledger: every reschedule supersedes the prior row and adds a
 *  new one, so this lists who was dispatched to the job on each date. */
export interface JobAppointment {
    id: string;
    jobId: string;
    startAt: string;
    endAt: string;
    assignedTechId: string | null;
    assignedTechName: string | null;
    priority: string;
    bookingConfirmedAt: string | null;
    supersededAt: string | null;
    supersededBy: string | null;
    /** How the appointment ended (Issue #454); null = the live/current one. */
    outcome: string | null;
    endedAt: string | null;
    /** Why the appointment ended (office's reason, e.g. "Locked / no access"). */
    endedReason: string | null;
    createdAt: string;
    crew: JobAppointmentCrewMember[];
}

/** Flexible crew: payload for POST /jobs/:id/assignments. */
export interface AddCrewPayload {
    userId: string;
    role: "tech" | "helper";
    earningMode: "full" | "split_even" | "mirror";
    force?: boolean;
    raiseCrewSizeTo?: number;
    reason?: string;
}

export const EARNING_MODE_LABEL: Record<"full" | "split_even" | "mirror", string> = {
    full: "Full job points",
    split_even: "Split evenly",
    mirror: "Helper mirror",
};

export interface TechOption {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
}

export function techLabel(t: TechOption): string {
    const full = `${t.first_name ?? ""} ${t.last_name ?? ""}`.trim();
    return full || t.email || "Unknown tech";
}

export type HelperValueTier = "low" | "medium" | "high";

export const HELPER_VALUE_LABEL: Record<HelperValueTier, string> = {
    low: "Low",
    medium: "Medium",
    high: "High",
};

export const HELPER_VALUE_BADGE_CLS: Record<HelperValueTier, string> = {
    low: "bg-slate-100 text-slate-700 border-slate-200",
    medium: "bg-sky-50 text-sky-700 border-sky-200",
    high: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export const SCHEDULING_FLEXIBILITY_LABEL: Record<string, string> = {
    flexible: "Flexible",
    specific_day: "Specific day",
    specific_window: "Specific window",
    hard_appointment: "Hard appointment",
};

// Trade → asset family mapping. Mirrors the canonical decision in the
// plan (Decisions captured): a backflow job's device picker only shows
// backflow assemblies, a fire-safety job's picker only shows fire
// extinguishers + emergency lights. Any other / NULL trade falls
// through so legacy jobs keep working.
export function assetKindsForTrade(tradeCode: string | null | undefined): string[] | null {
    if (tradeCode === "backflow") return ["backflow"];
    if (tradeCode === "fire_ext") return ["extinguisher", "e_light"];
    return null;
}

/**
 * Whether an asset belongs on a job of this trade. EXACT mirror of the
 * server's checkAssetMatchesTrade / reconcileJobAssetsForTrade rule:
 * NULL asset_type is grandfathered on backflow jobs (ST imports pre-date
 * the taxonomy — the dominant historical case is backflow), and rejected
 * on fire-safety jobs. Unknown/NULL trades don't filter.
 *
 * Every job-page surface (device list render, Add-device picker) must
 * use THIS predicate — a stricter render filter once hid a successfully
 * linked NULL-type device while the picker kept offering it (7-Eleven
 * #35515: "device added" toast, but it never appeared).
 */
export function assetAllowedOnTrade(
    tradeCode: string | null | undefined,
    assetType: string | null | undefined,
): boolean {
    const kinds = assetKindsForTrade(tradeCode);
    if (kinds == null) return true;
    if (assetType == null) return tradeCode === "backflow";
    return kinds.includes(assetType);
}

/**
 * Which linked devices the job page should render. A device that is genuinely
 * linked must NEVER be hidden (7-Eleven #35515, issue #179): the server allows
 * linking any asset to a trade-less job and never auto-unlinks it, so the
 * office page must show what's there rather than discarding it. For a known
 * family we filter to that family (mirroring the server reconciler); for an
 * unknown / NULL trade `assetAllowedOnTrade` returns true, so every linked
 * asset passes through and a genuinely empty job still reads empty.
 */
export function visibleJobAssets<T extends { asset_type?: string | null }>(
    tradeCode: string | null | undefined,
    rawAssets: T[],
): T[] {
    return rawAssets.filter((a) => assetAllowedOnTrade(tradeCode, a.asset_type));
}

export type DeviceFamily = "backflow" | "fire_ext" | "generic";

export function familyForTrade(tradeCode: string | null | undefined): DeviceFamily {
    if (tradeCode === "backflow") return "backflow";
    if (tradeCode === "fire_ext") return "fire_ext";
    return "generic";
}

export interface JobDetail {
    id: string;
    display_number: string | null;
    title: string;
    status: string;
    /** #293: job spans several visits — crew submits each trip, the rest carries. */
    is_multi_visit?: boolean | null;
    description?: string;
    customer_id: string;
    company_name: string;
    location_id: string | null;
    location_name?: string;
    location_address?: string;
    site_id: string | null;
    site_name?: string | null;
    site_address_line?: string | null;
    site_city?: string | null;
    site_latitude?: string | number | null;
    site_longitude?: string | number | null;
    site_phone?: string | null;
    site_account_number?: string | null;
    /** Purveyor / reporting-portal account number (`sites.water_account_number`). */
    site_water_account_number?: string | null;
    site_contact_name?: string | null;
    site_contact_phone?: string | null;
    water_provider_id?: string | null;
    water_provider_name?: string | null;
    /** `water_providers.slug` — "mckinney" asks Cleaned/Replaced on repairs. */
    water_provider_slug?: string | null;
    water_provider_display_name?: string | null;
    water_provider_report_name?: string | null;
    water_provider_submission_platform?: string | null;
    water_provider_portal_url?: string | null;
    water_provider_is_portal?: boolean | null;
    // Vitals row (2026-07-14): PO/WO reference pinned in the header strip.
    customer_po?: string | null;
    external_ref?: string | null;
    briefing_wo_number?: string | null;
    /** #887 — the one short line statements/invoices may print (tech titles
     *  keep door codes/urgency). Editable on the Details card. */
    customer_facing_description?: string | null;
    /** #887 — mirrored from the customer: jobs for this account can't be
     *  completed without a WO reference. Drives the amber Details-card hint. */
    customer_requires_wo?: boolean | null;
    /** #1030 — mirrored from the customer: this account is on service hold
     *  ("Do Not Service"). A job can reach this page without passing the New
     *  Job form (WO intake, leads, estimate conversion), so the warning is
     *  repeated on the job itself. */
    customer_service_hold?: boolean | null;
    customer_service_hold_reason?: string | null;
    /** The Quiet Toolbar (10/10) — mark state, see lib/job-marks.ts. */
    cannot_complete_reason?: string | null;
    tech_flag_reason?: string | null;
    /** "No work order yet" (2026-10-21) — services/jobWorkOrderMissing.ts. */
    wo_missing?: boolean | null;
    /** "Check the site" (2026-10-29) — services/woSiteCheck.ts. Held from dispatch while true. */
    site_check_needed?: boolean | null;
    parts_blocked?: boolean | null;
    fail_protocol_paused?: boolean | null;
    fail_protocol_cleared?: boolean | null;
    from_online_booking?: boolean | null;
    helper_results_to_review?: number | null;
    /** Raw vendor-WO briefing (present on kind='vendor_work_order' jobs). The
     *  intake classification inside it steers trade-keyed behavior (#672). */
    work_order_briefing?: {
        service_classification?: { service_type?: string | null } | null;
    } | null;
    customer_phone?: string | null;
    /** TAX VISIBILITY: customer chip on the job vitals strip. */
    customer_tax_status?: string | null;
    customer_tax_exempt_reason?: string | null;
    assigned_to: string | null;
    assigned_to_name?: string;
    scheduled_start?: string | null;
    scheduled_end?: string | null;
    /** Booking lock state — `confirmed` once dispatch locks the appointment. */
    dispatch_lock_state?: string | null;
    confirmed_at?: string | null;
    confirmed_by_name?: string | null;
    priority?: string | null;
    job_type_priority?: string | null;
    tags?: string[] | null;
    /** Helper crew (role='helper') resolved from job_assignments. */
    crew?: JobCrewMember[] | null;
    trade_code?: string | null;
    trade_name?: string | null;
    job_type_id?: string | null;
    job_type_code?: string | null;
    job_type_name?: string | null;
    scheduling_flexibility?: string | null;
    preferred_window?: string | null;
    due_by_date?: string | null;
    tech_instructions: string | null;
    /** 'vendor_work_order' unlocks the WO cards (brief, original scope). */
    kind?: string | null;
    /** Vendor-WO tech BRIEF (Prompt 24 splitter output) — shown to the tech in the field app. */
    tech_notes?: string | null;
    notes: string | null;
    notes_rich: string | null;
    /** Office-authored on-site brief (Markdown) — the one office→tech "what to
     *  do on site" channel, shown at the top of the office page + tech screen. */
    summary: string | null;
    created_at: string;
    updated_at: string;
    /** Lifecycle stamps (jobDetailHeaderQuery) — drive the stage strip. */
    en_route_at?: string | null;
    arrived_at?: string | null;
    work_started_at?: string | null;
    completed_at?: string | null;
    /** C2-5 (#1314) — Start Work → complete, from `job_performance`. NULL when
     *  the tech never tapped Start Work; render `—`, never 0.
     *  Definition: docs/bible/labour-numbers.md */
    on_site_minutes?: number | null;
    /** #1513 — set only when the completion reached us later than the tap that
     *  made it (a tech finishing out of coverage). `completed_at` stays the
     *  time he finished, which is what payroll and points run on. */
    completed_received_at?: string | null;
    /** #1513 — when the assigned tech's app last reached the server at all.
     *  Separates "still working" from "finished, no signal". */
    assigned_tech_last_seen_at?: string | null;
    helper_value_override?: HelperValueTier | null;
    helper_value?: HelperValueTier | null;
    helper_value_default?: HelperValueTier | null;
    source_estimate_id?: string | null;
    source_estimate_title?: string | null;
    source_estimate_display_number?: string | null;
    /** Approval record from the estimate magic-link flow — who the customer
     *  said they were on the token page, and when they clicked Approve. */
    source_estimate_approved_by?: string | null;
    source_estimate_approved_at?: string | null;
    source_estimate_status?: string | null;
}

// Legacy point-pay linkage (`devices` keyed off `location_id`). Canonical new
// jobs link devices via `assets` instead (see `JobAssetRow`).
export interface DeviceRow {
    device_id: string;
    label: string;
    size_inches: number;
    serial_number: string | null;
    device_status: string;
    required_action: string;
    sequence_no: number | null;
}

// Modern site-spined job ⇄ asset linkage. Each row represents one assembly the
// tech needs to work on at the site, with the `link_id` scoped to the join
// table so we can unlink without touching the asset record.
export interface JobAssetRow {
    link_id: string;
    asset_id: string;
    label: string;
    size_label: string | null;
    serial_number: string | null;
    manufacturer: string | null;
    model: string | null;
    location_description: string | null;
    location_lat: string | number | null;
    location_lng: string | number | null;
    asset_status: string;
    asset_type: string | null;
    assembly_type: string | null;
    service_type: string | null;
    service_type_other: string | null;
    meter_number: string | null;
    parent_asset_id: string | null;
    /** Serial of the bypass parent (DCDA/RPDA main) when this row is a bypass leg. */
    parent_serial_number: string | null;
    /** Non-null renders the amber "Verify on site" chip (authority-list reconcile). */
    needs_verification_reason: string | null;
    compliance_metadata: Record<string, unknown> | null;
    required_action: string;
    sequence_no: number | null;
    /** Server-computed: this device's action chain is finished (tested /
     *  repaired+tested / replacement finalized / inspected), or it's been
     *  disposed or its asset retired. Mirrors the completion gate, so the
     *  office Results-card progress can't disagree with what submit allows. */
    resolved?: boolean;
    /** Link provenance (`job_assets.linked_via`, migration 185).
     *  'wo_site_fallback' = the work order named no devices, so the location's
     *  list was used — the Results card says so and asks the office to confirm. */
    linked_via?: string | null;
    /** What the tech did with this device instead of testing it
     *  (`job_assets.disposition`) — e.g. not_located, could_not_test. */
    disposition?: string | null;
    /** #1142 — the tech's reason, mandatory on both untestable dispositions
     *  (migration 210). Office Review and the backflow workflow already showed
     *  it; the job page never asked the server for it. */
    disposition_reason?: string | null;
}

export interface LinkedInvoice {
    id: string;
    invoice_number?: string;
    title: string;
    status: string;
    total_amount?: number;
    subtotal?: number;
    created_at: string;
    /** Payment facts for the stage strip's Paid stage (ST import + native payments). */
    st_paid_on?: string | null;
    st_balance?: number | string | null;
    payments_total?: number | string | null;
    last_payment_at?: string | null;
    /** #1142 — how many times this invoice actually reached a customer, from
     *  the send ledger (`customer_send_emails`, successful sends only). The
     *  status enum has no 'sent' state, so it cannot answer this: an emailed
     *  invoice stays 'open' until it is paid. */
    emailed_count?: number;
    last_emailed_at?: string | null;
    /** Of those, how many SendGrid later reported as bounced / blocked (migration 626). */
    undelivered_count?: number;
    /** "address — why", for the most recent one that didn't arrive. */
    undelivered_note?: string | null;
}

export interface LinkedEstimate {
    id: string;
    display_number?: string | null;
    title: string;
    status: string;
    subtotal?: number;
    created_at: string;
    updated_at?: string | null;
    /** ST "sold on" — best available sent/approved timestamp for the Quote sent stage. */
    st_sold_on?: string | null;
}

export interface JobTestResult {
    id: string;
    asset_id: string;
    asset_name: string;
    serial_number?: string;
    assembly_type?: string;
    size_label?: string;
    manufacturer?: string;
    model?: string;
    overall_result: string;
    /** Trust audit Phase 1: the main leg's own result, and the bypass leg's
     *  (null when the row has no bypass readings). One row carries both. */
    main_leg_result?: "pass" | "fail" | null;
    bypass_leg_result?: "pass" | "fail" | null;
    recommended_action?: string;
    performed_at: string;
    remarks?: string;
    completion_notes?: string;
    /** The "why it failed" story on a FAIL row (Issue: failure readback):
     *  the per-component reasons the gauge readings produced, the tech's free
     *  note, and whether the office should spin up a repair. Absent on passes
     *  and on fire-safety rows. */
    failure_reasons?: string[] | null;
    failure_notes?: string | null;
    flagged_for_repair?: boolean | null;
    photos?: Record<string, string[]> | null;
    /** Attribution (Issue #269) — who to ask about this result. */
    performed_by_name?: string | null;
    /** External / portal-reported tester name (non-internal tests). */
    tester_name?: string | null;
    /** Helper who typed the row in Helper Mode, when different from performer. */
    entered_by_name?: string | null;
    /** #1300 — 'paper_conversion' when the office read this result off a signed
     *  paper form because the app workflow never finished. NULL on every row
     *  entered through the normal test screen. */
    entry_source?: string | null;
    /** Why the app flow couldn't finish (paper conversions only). */
    conversion_reason?: string | null;
    /** #1958 — a paper FAIL with no readings, said so outright. Shown in red. */
    no_readings_cant_file?: boolean | null;
    /** A paper FAIL sent to the tech; waiting on his readings (trust audit Phase 1). */
    paper_readings_waiting?: boolean | null;
    paper_readings_tech_name?: string | null;
    /** The office user who converted the paper — never the tester. */
    converted_by_name?: string | null;
    /** The signed form the result was read off. */
    paper_evidence_attachment_id?: string | null;
    /** Set when the tested device lives at a DIFFERENT site than the job
     *  (Sunny Woods #092678193). The card names and links that site. */
    other_site_id?: string | null;
    other_site_name?: string | null;
}

export interface JobRepairResult {
    id: string;
    asset_id: string;
    repairs_confirmed?: unknown;
    parts_used?: unknown;
    repair_notes?: string | null;
    created_at: string;
    /** Attribution (Issue #269) — the tech who performed the repair. */
    performed_by_name?: string | null;
    entered_by_name?: string | null;
    /** Set when the repair was saved without photos (tech's reason, or the
     *  office's auto-filled one). Null = photos were taken. */
    photos_skipped_reason?: string | null;
}

/** Vendor-WO jobs: intake item + matched-template mapping health, so the
 *  office can inspect/fix the return-PDF mapping straight from the job page
 *  (auto-created jobs never pass through the review screen). */
export interface WorkOrderMappingInfo {
    intake_item_id: string;
    auto_created: boolean;
    overall_confidence: string | number | null;
    template_id: string | null;
    template_name: string | null;
    mapped_field_count: number | null;
    requirements_confirmed: boolean | null;
}

export interface JobDetailResponse {
    job: JobDetail;
    devices: DeviceRow[];
    assets: JobAssetRow[];
    invoices?: LinkedInvoice[];
    estimates?: LinkedEstimate[];
    testResults?: JobTestResult[];
    repairResults?: JobRepairResult[];
    workOrder?: WorkOrderMappingInfo | null;
    /** The newest delivery the office recorded without email (2026-10-07). */
    deliveredAnotherWay?: DeliveredAnotherWay | null;
}

export interface DeliveredAnotherWay {
    channel: string;
    label: string | null;
    sentAt: string;
    sentByName: string | null;
}

export interface ReportAttachment {
    id: string;
    file_name: string;
    created_at: string;
    file_size?: number;
    /** Provenance note ("Original work order (intake)", …) — the API returns
     *  the full row; used to classify documents in the Paperwork filters. */
    notes?: string | null;
    /** #819 B8: human label (photo_tag column) — preferred over file_name
     *  wherever a person reads the list; the raw filename stays secondary. */
    photo_tag?: string | null;
    /** Photos only: which device it shows (`2" Double Check · SN 40613G · Tag
     *  photo`), built by the server from the device record — beats photo_tag. */
    photo_label?: string | null;
    /** Photos only: the human name it downloads under (`SN-40613G Tag 2026-10-26.jpg`). */
    download_name?: string | null;
    /** Storage category (`test_report_official`, `document`, …). #1538 reads it
     *  to decide whether a row may be renamed — generated names are derived and
     *  a regenerate would undo the edit. */
    category?: string | null;
    /** The device this file belongs to. An official report with none was
     *  filed against the job itself and the packet never counts it
     *  (#072659090) — "Make official" relinks it. */
    asset_id?: string | null;
    /** Who put the file there. `GET /api/attachments` returns it joined
     *  (`uploaded_by_name`); null on anything a workflow generated. */
    uploaded_by?: string | null;
    uploaded_by_name?: string | null;
}

/** Pull the `{ error, message }` body off an ApiError without leaning on
 *  `any` (the thrown error carries an unknown-typed `body`). */
export function apiErrorInfo(e: unknown): { error?: string; message?: string } {
    const body = (e as { body?: unknown } | null | undefined)?.body;
    if (body && typeof body === "object") return body as { error?: string; message?: string };
    return {};
}
